import { memo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { User, Bot, Database, Monitor, HardDrive, Server, ChevronRight, Search, Shield, AlertTriangle, X, GitBranch, FolderGit2, Maximize2 } from 'lucide-react';
import { GraphNodeData, GraphFlowNode, ConnectionChild } from './types';
import { NodeType } from '../../types/graph';
import awsLogo from '../../assets/aws-logo.svg';
import gcpLogo from '../../assets/gcp-logo.svg';
import azureLogo from '../../assets/azure-logo.svg';
import githubLogo from '../../assets/github-logo.svg';
import gitlabLogo from '../../assets/gitlab-logo.svg';
import { cn } from '../../lib/utils';

const cloudLogos: Record<string, string> = {
  aws: awsLogo,
  gcp: gcpLogo,
  azure: azureLogo,
};

const saasLogos: Record<string, string> = {
  github: githubLogo,
  gitlab: gitlabLogo,
};

const borderColors: Record<string, string> = {
  user: '#3b82f6',
  service_account: '#8b5cf6',
  resource: '#f59e0b',
  role: '#06b6d4',
  anomaly: '#ef4444',
};

const NodeIcon = ({ type, resourceType, isSaasResource }: { type: string; resourceType?: string; isSaasResource?: boolean }) => {
  const iconClass = "w-8 h-8";

  if (isSaasResource) {
    if (resourceType === 'repository') return <GitBranch className={iconClass} />;
    return <FolderGit2 className={iconClass} />;
  }
  if (type === 'user') return <User className={iconClass} />;
  if (type === 'service_account') return <Bot className={iconClass} />;
  if (type === 'resource') {
    if (resourceType === 'database') return <Database className={iconClass} />;
    if (resourceType === 'virtual-machine') return <Monitor className={iconClass} />;
    if (resourceType === 'storage-bucket') return <HardDrive className={iconClass} />;
    return <Server className={iconClass} />;
  }
  return <Server className={iconClass} />;
};

const getChildIcon = (type: NodeType, resourceType?: string) => {
  if (type === 'role') return Shield;
  if (type === 'user') return User;
  if (type === 'service_account') return Bot;
  if (type === 'resource') {
    if (resourceType === 'database') return Database;
    if (resourceType === 'virtual-machine') return Monitor;
    if (resourceType === 'storage-bucket') return HardDrive;
    return Server;
  }
  if (type === 'anomaly') return AlertTriangle;
  return Server;
};

export const CardNode = memo(({ data, selected }: NodeProps<GraphFlowNode>) => {
  const nodeData = data as GraphNodeData;

  // Detect SaaS resource nodes (from correlations/blast radius modes)
  const isSaasResource = !!(nodeData as any).isSaasResource;
  const saasType = ((nodeData as any).saasType || '').toLowerCase();

  const borderColor = isSaasResource
    ? (saasType === 'github' ? '#24292f' : saasType === 'gitlab' ? '#e24329' : borderColors[nodeData.type] || '#6b7280')
    : (borderColors[nodeData.type] || '#6b7280');

  // Get provider from node data's cloudProvider prop (set during transform)
  const provider = String(nodeData.cloudProvider || nodeData.provider || 'gcp');
  const activityPercent = nodeData.activity !== undefined ? Math.min(100, Math.max(0, nodeData.activity)) : 50;
  const [searchQuery, setSearchQuery] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calculate activity bar color
  let activityBarColor: string;
  if (activityPercent <= 50) {
    const ratio = activityPercent / 50;
    const r = Math.round(34 + (234 - 34) * ratio);
    const g = Math.round(197 + (179 - 197) * ratio);
    const b = Math.round(8 + (8 - 94) * ratio);
    activityBarColor = `rgb(${r}, ${g}, ${b})`;
  } else {
    const ratio = (activityPercent - 50) / 50;
    const r = Math.round(234 + (239 - 234) * ratio);
    const g = Math.round(179 + (68 - 179) * ratio);
    const b = Math.round(8 + (68 - 8) * ratio);
    activityBarColor = `rgb(${r}, ${g}, ${b})`;
  }

  // Badge text and resource type display for SaaS vs cloud resources
  let typeText: string;
  let displayResourceType = '';

  if (isSaasResource) {
    const rt = ((nodeData as any).saasResourceType || nodeData.resourceType || '').toLowerCase();
    if (rt === 'repository') typeText = 'REPOSITORY';
    else if (rt === 'pipeline') typeText = 'PIPELINE';
    else typeText = rt ? rt.toUpperCase() : 'SAAS RESOURCE';
  } else {
    typeText = nodeData.type.replace('_', ' ').toUpperCase();

    if (nodeData.type === 'resource' && nodeData.resourceType) {
      if (nodeData.resourceType === 'database') displayResourceType = 'Database';
      else if (nodeData.resourceType === 'virtual-machine') displayResourceType = 'Virtual Machine';
      else if (nodeData.resourceType === 'storage-bucket') displayResourceType = 'Storage Bucket';
      else displayResourceType = nodeData.resourceType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
  }

  // Count badges - always show (static), Alerts only red if count > 0
  const topRowItems: { label: string; count: number; isAlert: boolean }[] = [];
  const bottomRowItems: { label: string; count: number; isAlert: boolean }[] = [];

  if (isSaasResource) {
    topRowItems.push({ label: 'Alerts', count: nodeData.alertCount || 0, isAlert: (nodeData.alertCount || 0) > 0 });
    topRowItems.push({ label: 'Users', count: nodeData.userCount || 0, isAlert: false });
  } else if (nodeData.type === 'user' || nodeData.type === 'service_account') {
    topRowItems.push({ label: 'Alerts', count: nodeData.alertCount || 0, isAlert: (nodeData.alertCount || 0) > 0 });
    topRowItems.push({ label: 'Roles', count: nodeData.roleCount || 0, isAlert: false });
    bottomRowItems.push({ label: 'Resources', count: nodeData.resourceCount || 0, isAlert: false });
  } else if (nodeData.type === 'resource') {
    topRowItems.push({ label: 'Alerts', count: nodeData.alertCount || 0, isAlert: (nodeData.alertCount || 0) > 0 });
    topRowItems.push({ label: 'Users', count: nodeData.userCount || 0, isAlert: false });
  }

  // Connection panel logic
  const isOpen = nodeData.isConnectionPanelOpen;
  const allChildren = nodeData.connectionChildren || [];
  const expandedChildIds = nodeData.expandedChildIds || new Set<string>();

  // Filter out already expanded children AND exclude alerts from CardNode connection panel
  // (Alerts are auto-displayed attached to their parent nodes)
  const children = allChildren.filter(child =>
    !expandedChildIds.has(child.id) && child.type !== 'anomaly'
  );
  const totalChildren = children.length;

  // Filter children by search
  const filteredChildren = searchQuery
    ? children.filter(child =>
      child.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      child.type?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : children;

  // Reset search when panel closes
  useEffect(() => {
    if (!isOpen) setSearchQuery('');
  }, [isOpen]);

  useEffect(() => () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setIsHovered(false), 250);
  };

  const handleChildClick = (childId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onSelectChild?.(childId);
  };

  return (
    <div
      className="relative cursor-pointer transition-transform duration-200 hover:scale-105"
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {nodeData.hasUnexpandedChildren && (
        <>
          {/* Third stack (back) */}
          <div
            className="absolute rounded-[32px] border-[3px] inset-0"
            style={{
              borderColor,
              backgroundColor: 'var(--background)',
              transform: 'translate(24px, 24px)',
              opacity: 0.3,
              boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
            }}
          />
          {/* Second stack (middle) */}
          <div
            className="absolute rounded-[32px] border-[3px] inset-0"
            style={{
              borderColor,
              backgroundColor: 'var(--background)',
              transform: 'translate(14px, 14px)',
              opacity: 0.5,
              boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
            }}
          />
          {/* First stack (front-most behind main) */}
          <div
            className="absolute rounded-[32px] border-[3px] inset-0"
            style={{
              borderColor,
              backgroundColor: 'var(--background)',
              transform: 'translate(7px, 7px)',
              opacity: 0.7,
              boxShadow: `0 4px 12px rgba(0,0,0,0.15)`,
            }}
          />
        </>
      )}

      {nodeData.canShowFullGraph && (
        <button
          type="button"
          className={cn(
            "nodrag nopan absolute left-1/2 top-full z-20 mt-8 -translate-x-1/2 flex items-center gap-3 whitespace-nowrap rounded-full border-2 border-primary/25 bg-background/95 px-8 py-4 text-3xl font-semibold text-primary shadow-xl backdrop-blur transition-all duration-200 hover:bg-primary hover:text-primary-foreground",
            isHovered ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={(e) => {
            e.stopPropagation();
            nodeData.onShowFullGraph?.(nodeData.nodeId || '');
          }}
        >
          <Maximize2 className="h-8 w-8" />
          Show Full Graph
        </button>
      )}

      {/* Type badge - outside the card */}
      <div
        className="absolute flex items-center gap-3 px-6 py-3 rounded-lg text-white text-2xl font-bold shadow-md"
        style={{
          backgroundColor: borderColor,
          top: -60,
          left: 0,
        }}
      >
        <NodeIcon type={nodeData.type} resourceType={nodeData.resourceType} isSaasResource={isSaasResource} />
        <span>{typeText}</span>
      </div>

      {/* Main card - rectangular shape */}
      <div
        className={`relative w-[640px] min-h-[220px] rounded-[32px] bg-background border-[3px] shadow-lg p-8 transition-all ${selected ? 'ring-4 ring-primary shadow-[0_0_30px_rgba(59,130,246,0.5)]' : ''
          } ${nodeData.isHighlighted ? 'ring-4 ring-primary shadow-[0_0_30px_rgba(59,130,246,0.5)]' : ''}`}
        style={{
          borderColor,
        }}
      >
        {/* Invisible handles at center for edge connections */}
        <Handle
          type="target"
          position={Position.Left}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ left: '50%', top: '50%' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ right: '50%', top: '50%' }}
        />

        {/* Logo: SaaS logo for SaaS resources, cloud logo otherwise */}
        <div className="absolute top-5 right-5">
          {isSaasResource && saasLogos[saasType] ? (
            <img
              src={saasLogos[saasType]}
              alt={saasType}
              className={`w-16 h-16 ${saasType === 'github' ? 'dark:invert' : ''}`}
            />
          ) : (
            <img
              src={cloudLogos[provider.toLowerCase()] || cloudLogos.gcp}
              alt={provider}
              className="w-16 h-16"
            />
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {/* Resource type label for cloud resources / SaaS type for SaaS resources */}
          {isSaasResource && saasType && (
            <span className="text-2xl font-semibold text-muted-foreground capitalize">
              {saasType.charAt(0).toUpperCase() + saasType.slice(1)} Repository
            </span>
          )}
          {!isSaasResource && displayResourceType && (
            <span className="text-2xl font-semibold text-muted-foreground">
              {displayResourceType}
            </span>
          )}

          {/* Name - for Azure/AWS service accounts, show only the part after the last '/' */}
          <span className="text-4xl font-semibold text-foreground pr-24 block leading-tight pt-1 break-words overflow-hidden">
            {nodeData.type === 'service_account' && (provider.toLowerCase() === 'azure' || provider.toLowerCase() === 'aws')
              ? (nodeData.label.split('/').pop() || nodeData.label)
              : nodeData.label}
          </span>

          {/* Email - now wrapping instead of truncating */}
          {nodeData.email && (
            <span className="text-2xl text-muted-foreground leading-tight break-all mt-2">
              {nodeData.email}
            </span>
          )}

          {/* Count badges */}
          <div className="flex flex-wrap gap-3 mt-5">
            {topRowItems.map((item, i) => (
              <span
                key={i}
                className={`text-2xl font-bold px-5 py-2 rounded ${item.isAlert
                  ? 'bg-red-500/20 text-red-500'
                  : 'bg-muted text-muted-foreground'
                  }`}
              >
                {item.count} {item.label}
              </span>
            ))}
          </div>
          {bottomRowItems.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-3">
              {bottomRowItems.map((item, i) => (
                <span
                  key={i}
                  className="text-2xl font-bold px-5 py-2 rounded bg-muted text-muted-foreground"
                >
                  {item.count} {item.label}
                </span>
              ))}
            </div>
          )}

          {/* Activity bar - hide for SaaS resources */}
          {!isSaasResource && (
            <div className="mt-5 pt-3">
              <div className="flex justify-between text-2xl text-muted-foreground mb-2">
                <span>Activity</span>
                <span>{Math.round(activityPercent)}%</span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${activityPercent}%`,
                    backgroundColor: activityBarColor
                  }}
                />
              </div>
              {nodeData.lastActivityMessage && (
                <span className="text-2xl text-muted-foreground mt-3 block truncate">
                  {nodeData.lastActivityMessage}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expand button on right side - hidden in correlations/blast radius mode */}
      {!nodeData.hideExpandButton && ((nodeData.childCount ?? 0) > 0 || (nodeData.connectionChildren?.length ?? 0) > 0) && (
        <div
          className={cn(
            "absolute flex items-center justify-center rounded-r-2xl cursor-pointer transition-all",
            isOpen ? "opacity-70" : "hover:opacity-90"
          )}
          style={{
            backgroundColor: borderColor,
            right: -40,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 40,
            height: 80,
          }}
          onClick={(e) => {
            e.stopPropagation();
            nodeData.onExpandClick?.(nodeData.nodeId || '');
          }}
        >
          <ChevronRight className={cn("w-8 h-8 text-white transition-transform", isOpen && "rotate-90")} />
        </div>
      )}

      {/* Connection dropdown - integrated into node */}
      {isOpen && children.length > 0 && (
        <div
          ref={panelRef}
          className="absolute nodrag nowheel"
          style={{
            top: '50%',
            left: 680,
            transform: 'translateY(-50%)',
            width: 560,
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="rounded-3xl shadow-2xl border-2 bg-card">
            {/* Header with search */}
            <div className="px-6 py-5 border-b bg-muted/50 flex items-center gap-4 rounded-t-3xl">
              <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-background border">
                <Search size={24} className="text-muted-foreground" />
                <input
                  type="text"
                  placeholder={`Search connections...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 text-xl bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nodeData.onClosePanel?.();
                }}
                className="p-3 rounded-xl transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
              >
                <X size={24} />
              </button>
            </div>

            {/* Children list - scrollable dropdown with wheel event capture */}
            <div
              className="overflow-y-auto nowheel"
              style={{ maxHeight: '480px' }}
              onWheel={(e) => e.stopPropagation()}
            >
              {filteredChildren.length === 0 ? (
                <div className="px-6 py-12 text-center text-xl text-muted-foreground">
                  No connections found
                </div>
              ) : (
                filteredChildren.map((child) => {
                  const Icon = getChildIcon(child.type, child.resourceType);
                  const childBorderColor = borderColors[child.type] || '#6b7280';

                  return (
                    <div
                      key={child.id}
                      onClick={(e) => handleChildClick(child.id, e)}
                      className={cn(
                        "px-6 py-6 flex items-center gap-5 cursor-pointer transition-colors border-b last:border-b-0",
                        "hover:bg-muted/50"
                      )}
                      style={{
                        borderLeftWidth: 6,
                        borderLeftColor: childBorderColor,
                      }}
                    >
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${childBorderColor}15`, border: `2px solid ${childBorderColor}25` }}
                      >
                        <Icon size={32} style={{ color: childBorderColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-2xl font-medium truncate text-foreground">
                          {child.label}
                        </p>
                        <p className="text-lg truncate capitalize text-muted-foreground">
                          {child.type.replace('_', ' ')}
                        </p>
                      </div>
                      {child.hasAnomaly && (
                        <div
                          className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0"
                          title="Has anomaly"
                        >
                          <AlertTriangle size={24} className="text-red-500" />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* +X more button - positioned relative to card bottom */}
      {nodeData.collapsedCount > 0 && (
        <div
          className="absolute left-1/2 -translate-x-1/2 px-5 py-2.5 rounded text-white text-xl font-bold cursor-pointer hover:opacity-90 -bottom-12"
          style={{
            backgroundColor: borderColor,
          }}
        >
          +{nodeData.collapsedCount} more
        </div>
      )}
    </div>
  );
});

CardNode.displayName = 'CardNode';
